import { store } from "stores/Store";
import styles from './Details.module.scss';
import Select from "react-select";
import React, { useEffect, useRef, useState } from "react";
import { Tab } from "Modules/PractitionerBaseballCard";
import Text from "components/Text/Text";
import { observer } from "mobx-react";
import { ReactComponent as NotesIcon } from "assets/notes.svg";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Box, Button, Center, Group, Modal, ScrollArea, Stack, Title } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import "@mantine/carousel/styles.css";
import { getStreetViewUrlsFromAddress, getStreetViewUrlsFromCoords } from "helpers/streetView";
import { AddressFormData } from "Modules/PractitionerBaseballCard/components/AddressForm";
import GeoValidationDrawer from "components/GeoValidationDrawer";
import useExternalApiService from "helpers/ExternalApiService";
import { ReactComponent as PlusIcon } from "assets/plus.svg";
import TextWithLineBreaks from "components/TextWithBreaklines";
import CopyActionSheet from "Modules/PractitionerBaseballCard/components/CopyActionSheet";
import { copyToClipboard } from "helpers/clipboard";
import Toastr from "components/Toastr";

type GeoValidationAction = "validate" | "draft" | "";


// Helper: translate raw data_source → friendly text
const getDataSourceLabel = (ds?: string): string => {
  switch (ds) {
    case "PRACTITIONER_ACCOUNT_VIEW":
      return "From Netsuite";
    case "PRACTITIONER_ACCOUNT_VIEW_NM":
      return "From Badger Export";
    case "PRACTITIONER_DISTRIBUTOR_ACCOUNT_VIEW":
      return "Distributor";
    default:
      return ds || "Unknown"; // fallback if undefined or unrecognized
  }
};

const getChannelLabel = (ds?: string): string => {
  switch (ds) {
    case "PRACTITIONER_ACCOUNT_VIEW_NM":
      return "Not in Netsuite";
    default:
      return ds || ""; // fallback if undefined or unrecognized
  }
};

// Helper functions for conditional labels based on country
const getCityLabel = (country?: string): string => {
  return country === "AU" ? "Suburb" : "City";
};

const getStateLabel = (country?: string): string => {
  if (country === "CA") return "Province/State";
  if (country === "AU") return "State/Territory";
  return "State";
};

const getZipcodeLabel = (country?: string): string => {
  return country === "US" ? "Zipcode" : "Postal Code";
};



const handleContact = (opt: string, phone?: string) => {
  switch (opt) {
    case 'email':
      window.open(`mailto:${store.currentPractitioner?.email}`, '_blank');
      break;
    case 'phone':
      window.open(`tel:${phone}`, '_blank');
      break;

  }
}
interface IPractitionerDetails {
  navigate: (tab: Tab) => void;
  onOpenAddressForm: (practitionerId: string, addressId?: string, initialData?: Partial<AddressFormData>) => void;
  onAddressChange?: () => void;
}

const checkPhoneAddress = (phone: string) => {

  if (phone) {
    return phone;
  }
  if (store.currentPractitioner?.addresses && store.currentPractitioner?.addresses.length > 0) {

    const defaultAddress = store.currentPractitioner.addresses.find(
      (a: any) => a.id === store.currentPractitioner.default_address
    );
    if (defaultAddress?.phone) {
      return defaultAddress.phone;
    }
    const otherAddress = store.currentPractitioner.addresses.find(
      (a: any) => a.id !== store.currentPractitioner.default_address && a.phone
    );
    if (otherAddress?.phone) {
      return otherAddress.phone;
    }
  }
  return null;
}

const PractitionerDetails = observer(({ navigate, onOpenAddressForm, onAddressChange }: IPractitionerDetails) => {
  const [distance, setDistance] = React.useState<number | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<any>();
  const [streetViewPhotos, setStreetViewPhotos] = useState<string[]>([]);
  const [isLoadingStreetView, setIsLoadingStreetView] = useState<boolean>(false);
  const [isCarouselOpen, setIsCarouselOpen] = useState<boolean>(false);
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { patchData, putData, postData } = useExternalApiService(apiUrl);
  const isLead = ((store.currentPractitioner?.status === "LEAD-READY2SUBMIT") || (store.currentPractitioner?.status === "LEAD-INCOMPLETE"))
  const lat = selectedAddress?.latitude;
  const lon = selectedAddress?.longitude;
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [actionSheetType, setActionSheetType] = useState<'email' | 'phone' | 'website' | null>(null);
  const [showCopyToast, setShowCopyToast] = useState(false);

  // Helper to generate long-press copy props
  const getLongPressCopyProps = (value: string | undefined | null, timerKey: string) => ({
    style: { cursor: value ? 'pointer' : 'default' },
    onTouchStart: () => {
      if (!value) return;
      const timer = setTimeout(() => {
        copyToClipboard(value);
        setShowCopyToast(true);
      }, 500);
      (window as any)[timerKey] = timer;
    },
    onTouchEnd: () => clearTimeout((window as any)[timerKey]),
    onTouchCancel: () => clearTimeout((window as any)[timerKey]),
    onMouseDown: () => {
      if (!value) return;
      const timer = setTimeout(() => {
        copyToClipboard(value);
        setShowCopyToast(true);
      }, 500);
      (window as any)[timerKey] = timer;
    },
    onMouseUp: () => clearTimeout((window as any)[timerKey]),
    onMouseLeave: () => clearTimeout((window as any)[timerKey]),
  });

  React.useEffect(() => {

  }, []);

  useEffect(() => {
    if (store.currentPractitioner?.addresses && store.currentPractitioner?.addresses.length > 0) {
      // If we already have a selected address, update it with the new data
      if (selectedAddress?.id) {
        const updatedAddress = store.currentPractitioner.addresses.find(
          (a: any) => a.id === selectedAddress.id
        );
        if (updatedAddress) {
          setSelectedAddress(updatedAddress);
          return;
        }
      }
      // Otherwise, set the default address
      const defaultAddress = store.currentPractitioner.addresses.find(
        (a: any) => a.id === store.currentPractitioner.default_address
      );
      if (defaultAddress) {
        setSelectedAddress(defaultAddress);
      } else {
        setSelectedAddress(store.currentPractitioner.addresses[0]);
      }
    } else {
      setSelectedAddress(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.currentPractitioner]);

  // EIS-1041: allow geo-validation when either the structured street
  // (`address1`) is populated, OR the legacy `address` blob looks like a real
  // street (contains a digit and is materially longer than city+state+zip).
  // The latter unblocks legacy/NetSuite-imported records where the street
  // only lives in the blob, while still blocking pure city/state/zip blobs
  // that would otherwise geocode to the town centroid.
  const canGeoValidate = (() => {
    if (selectedAddress?.address1?.trim()) return true;
    const blob = selectedAddress?.address?.trim() ?? '';
    if (!blob || !/\d/.test(blob)) return false;
    const cityStateZipLen = (
      (selectedAddress?.city ?? '') +
      (selectedAddress?.state ?? '') +
      (selectedAddress?.zipcode ?? '')
    ).replace(/\s/g, '').length;
    return blob.replace(/\s/g, '').length > cityStateZipLen + 5;
  })();

  useEffect(() => {
    if (selectedAddress && (!selectedAddress?.latitude || !selectedAddress?.longitude) &&
      canGeoValidate) {
      handleGeoValidation();
    }

    if (selectedAddress) {
      calculteDistance()?.then((distance: number | null) => {
        if (distance !== null) {
          setDistance(distance);
        }
      });
    }
    setPhoneNumber(checkPhoneAddress(selectedAddress?.phone) || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress]);

  console.log("store.currentPractitioner=", store.currentPractitioner);
  const [geoValidationAction, setGeoValidationAction] = useState<GeoValidationAction>("");
  const handleGeoValidation = (action: GeoValidationAction = "validate") => {
    setGeoValidationAction(action);
  };

  const calculteDistance = () => {
    if (!selectedAddress?.latitude || !selectedAddress?.longitude) {
      return null;
    }

    // Get current position from browser
    if ("geolocation" in navigator) {
      console.log('hasnav');
      return new Promise<number>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition((position) => {
          console.log("calculating", selectedAddress);
          const lat1 = position.coords.latitude;
          const lon1 = position.coords.longitude;
          const lat2 = selectedAddress?.latitude;
          const lon2 = selectedAddress?.longitude;

          const R = 6371; // Radius of earth in km
          const dLat = (lat2 - lat1) * Math.PI / 180;
          const dLon = (lon2 - lon1) * Math.PI / 180;
          const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          const distance = R * c;

          resolve(Math.round(distance * 0.621371)); // Convert to miles and round
        }, (error) => {
          reject(error);
        });
      });
    }

    return null;
  }

  const handleStreetViewClick = async () => {
    if (!selectedAddress?.latitude || !selectedAddress?.longitude) {
      console.log('No coordinates available for street view');
      return;
    }
    console.log('clicked street view', selectedAddress);

    setIsLoadingStreetView(true);
    
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        console.error('Google Maps API key not configured');
        return;
      }
      let streetViewUrls;


      if (selectedAddress.address ) {
        streetViewUrls = await getStreetViewUrlsFromAddress(
          selectedAddress.address, 
          selectedAddress.latitude, 
          selectedAddress.longitude, 
          apiKey
        )
      }
      if (selectedAddress.latitude && selectedAddress.longitude && !streetViewUrls) {
      (streetViewUrls = await getStreetViewUrlsFromCoords(
        selectedAddress.latitude, 
        selectedAddress.longitude, 
        apiKey
      ))
      }
      if (streetViewUrls && streetViewUrls.length > 0) {
        setStreetViewPhotos(streetViewUrls);
      } else {
        console.log('Street View not available for this location');
      }
    } catch (error) {
      console.error('Error getting Street View:', error);
    } finally {
      setIsLoadingStreetView(false);
    }
  }
  

  return (
    <div className={styles.wrapper}>
      <GeoValidationDrawer
        opened={!!geoValidationAction}
        onClose={() => setGeoValidationAction("")}
        addressToValidate={
          selectedAddress?.address1 ? {
            administrativeArea: (selectedAddress?.state as any)?.value || "",
            locality: selectedAddress?.city || "",
            postalCode: selectedAddress?.zipcode || "",
            regionCode: selectedAddress?.country || "",
            addressLines: selectedAddress?.address1 ? [selectedAddress?.address1 || "", selectedAddress?.address2 || ""] : [selectedAddress?.address, ""],
          } : {
            administrativeArea: selectedAddress?.state  || "",
            locality: selectedAddress?.city || "",
            postalCode: selectedAddress?.zipcode || "",
            regionCode: selectedAddress?.country || "",
            addressLines: [selectedAddress?.address, ""],
          }
        }
        onConfirm={(a) => {
          if (a) {
            patchData(`/api/practitioner/${store.currentPractitioner?.id}/address/${selectedAddress.id}`, {
              address_data: {
                latitude: (a.latLon ? a.latLon[0] : undefined),
                longitude: (a.latLon ? a.latLon[1] : undefined),
              }
            }).then(() => {
              postData('/api/get_practitioner_by_id', {
                practitioner_id: store.currentPractitioner?.id,
              }).then((response) => {
                store.clearPractitionersCache();
                store.setCurrentPractitioner(response.practitioner);
              })

            });
          }
          setGeoValidationAction("");
        }}
      />
      {/*<div className={styles.sectionWrapper}>
        <p className={styles.headerText}>CONTACT INFORMATION</p>
      </div>*/}


      {!isLead ? (
        <Box>
          <Group justify="space-between" my="md">
            <Box c="dimmed">Default Check-in Address</Box>
            {store.currentPractitioner?.default_address !== selectedAddress?.id ? (
              <Button
                h={20}
                bg="#4F7D9C"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  putData(`/api/add_practitioner_to_chat/${store.currentPractitioner?.id}`, {
                    practitioner_data: {
                      default_address: selectedAddress?.id
                    }
                  });
                  store.clearPractitionersCache();
                  store.setCurrentPractitioner({
                    ...store.currentPractitioner,
                    default_address: selectedAddress?.id
                  });
                  store.setSelectedPractitioners(
                    store.selectedPractitioners?.map(
                      (sp) => {
                        if (sp?.id === store.currentPractitioner?.id) {
                          return {
                            ...sp,
                            default_address: selectedAddress?.id
                          };
                        }
                        return sp;
                      }
                    )
                  )
                  if (onAddressChange) {
                    onAddressChange();
                  }
                }}>
                SET
              </Button>
            ) : null }
          </Group>
          <Select
            value={selectedAddress}
            menuPlacement="auto"
            onChange={(v: any) => {
              if (v?.value) {
                const a = store.currentPractitioner.addresses.find((a: any) => a.id === v.value);
                if (a) {
                  setSelectedAddress(a);
                }
              }
            }}
            placeholder="Select an Address"
            components={{
              Control: (e) => {
                return (
                  <Group
                    style={{border: "1px solid #ddd", borderRadius: "10px"}}
                    align="center"
                    onClick={() => {
                      if (e.selectProps.menuIsOpen) {
                        e.selectProps.onMenuClose();
                      } else {
                        e.selectProps.onMenuOpen();
                      }
                    }}>
                    {e.children}
                  </Group>
                )
              },
              SingleValue: (e) => {
                const containerRef = useRef();

                const handleClickOutside = (event: any) => {
                  if (containerRef.current && !(containerRef.current as HTMLElement).contains(event.target)) {
                    e.selectOption(e.data);
                  }
                }

                useEffect(() => {
                  document.addEventListener('mousedown', handleClickOutside);
                  return () => {
                    document.removeEventListener('mousedown', handleClickOutside);
                  };
                  // eslint-disable-next-line react-hooks/exhaustive-deps
                }, []);

                console.log("selectedAddress", selectedAddress);
                
                const addresses = store.currentPractitioner?.addresses;
                if (!selectedAddress || !addresses?.length || !addresses[0]?.id) {
                  return (
                    <Box
                      w="100%"
                      mt={0}
                      ref={containerRef as any}>
                      <Title order={5}c="gray">
                        No addresses found
                      </Title>
                    </Box>
                  );
                }
                
                return (
                  <Box
                    w="100%"
                    mt={0}
                    ref={containerRef as any}>
                    <Title order={5} w="100%" c="dark">
                      {selectedAddress?.is_visiting_address ? (
                        `VISITING ADDRESS ${e.options
                          .filter((oo) => store.currentPractitioner?.addresses?.find((a: any) => a.id === oo.value)?.is_visiting_address)
                          .findIndex((oo: any) => oo.value === selectedAddress.id) + 1}`
                      ) : selectedAddress?.data_source === "PRACTITIONER_ACCOUNT_VIEW" ? (
                        `SHIPPING/BILLING ADDRESS (OLD BACKUP)` 
                      ) :(
                        `SHIPPING/BILLING ADDRESS`
                      )}
                    </Title>
                  </Box>
                )
              },
              Input: (e) => {
                return <></>;
              },
              MenuList: (o) => {
                return (
                  <Box mah={300} style={{overflowY: "scroll"}} >
                    {o.children}
                  </Box>
                )
              },
              Option: (o) => {
                const address = store.currentPractitioner.addresses.find((a: any) => a.id === (o as any).value);
                if (!address || !address.id) return <></>;

                return (
                  <Box
                    w="95%"
                    my={5}
                    style={{
                      borderBottom: "1px solid #eee",
                      padding: "10px",
                    }}
                    bg={(o as any)?.value === selectedAddress?.id ? "#E3F4FF" : "auto"}
                    onClick={() => o.selectOption(o.data)}>
                    <Title order={5} w="100%" c="dark" mb={2}>
                      {address?.is_visiting_address ? (
                        `VISITING ADDRESS ${o.options
                          .filter((oo) => store.currentPractitioner?.addresses?.find((a: any) => a.id === oo.value)?.is_visiting_address)
                          .findIndex((oo) => oo.value === (o as any).value) + 1}`
                      ) : (
                        `SHIPPING ADDRESS`
                      )}
                    </Title>
                    <Box pos="relative">
                      <Title order={6} fw="normal" c="gray">
                        {address.address}
                        <br />
                        {address.phone}
                      </Title>
                    </Box>
                  </Box>
                );
              }
            }}
            styles={{
              control: (baseStyles: any) => ({
                ...baseStyles,
                border: 'none',
                borderBottom: '1px solid #ccc',
                paddingLeft: 0,
                marginLeft: 0,
                color: '#4F7D9C',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none'
                }
              }),
              valueContainer: (baseStyles: any) => ({
                ...baseStyles,
                paddingLeft: 0,
                marginLeft: 10,
                color: '#4F7D9C',
              }),
              indicatorSeparator: () => ({
                display: 'none'
              }),
              menu: (baseStyles: any) => ({
                ...baseStyles,
                zIndex: 999,
              }),
            }}
            options={store.currentPractitioner?.addresses
              ?.slice()
              ?.sort((a1: any, a2: any) => a1.id - a2.id)
              ?.map((a: any) => ({
              label: a.address,
              value: a.id
            }))}
          />

          <Group my="lg" grow px={10}>
            {selectedAddress?.is_visiting_address ? (
              <Button
                bg="#FF9D00"
                size="xs"
                leftSection={<NotesIcon width={15} style={{ stroke: "white" }} />}
                onClick={() => {
                  onOpenAddressForm(store.currentPractitioner.id, selectedAddress.id, selectedAddress);
                }}>
                Edit Address
              </Button>
            ) : null}
            <Button
              bg="#00DC49"
              size="xs"
              leftSection={<PlusIcon width={15} />}
              onClick={() => {
                onOpenAddressForm(store.currentPractitioner.id);
              }}>
              Add Address
            </Button>
          </Group>
        </Box>
      ) : null}


      <div className={styles.twoColumnWrapper}>
        <div>
          <Text type="primary" bold fontSize="16px">Phone</Text>
          {phoneNumber ? (
            <span style={{ cursor: 'pointer' }} onClick={() => setActionSheetType('phone')}>
              <Text>{phoneNumber}</Text>
            </span>
          ) : (
            <div className={styles.missingValue}>Missing</div>
          )}
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Is Residential?</Text>
          <Text>{selectedAddress?.is_residential ? "Yes" : "No"}</Text>
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Street Address</Text>
          {selectedAddress?.address1?.trim() ? (
            <div {...getLongPressCopyProps(
              [selectedAddress?.address1, selectedAddress?.address2].filter(Boolean).join(' '),
              '__streetAddressCopyTimer'
            )}>
              <Text>{selectedAddress?.address1}</Text>
              {selectedAddress?.address2 && (<Text>{selectedAddress?.address2}</Text>)}
            </div>
          ) : (
            <div className={styles.missingValue}>MISSING</div>
          )}
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Full Address</Text>
          {/*<Text type="primary">(For Error Checking: Will Remove in Future)</Text>*/}
          {(selectedAddress?.address) ? (
            <div {...getLongPressCopyProps(selectedAddress?.address, '__addressCopyTimer')}>
              <TextWithLineBreaks text={`${selectedAddress?.address} `} />
            </div>
          ) : (
            <div className={styles.missingValue}>MISSING</div>
        )}

        </div>

      </div>


      <div className={styles.twoColumnWrapper}>
        <div>
          <Text type="primary" bold fontSize="16px">{getCityLabel(selectedAddress?.country)}</Text>
          {selectedAddress?.city ? (
            <div {...getLongPressCopyProps(selectedAddress?.city, '__cityCopyTimer')}>
              <Text>{`${selectedAddress?.city}`}</Text>
            </div>
          ) : (
            <div className={styles.missingValue}>MISSING</div>
          )}
        </div>

        <div>
          <Text type="primary" bold fontSize="16px">{getStateLabel(selectedAddress?.country)}</Text>
          {selectedAddress?.state ? (
            <div {...getLongPressCopyProps(selectedAddress?.state, '__stateCopyTimer')}>
              <Text>{`${selectedAddress?.state}`}</Text>
            </div>
          ) : (
            <div className={styles.missingValue}>MISSING</div>
          )}
        </div>
      </div>
      <div className={styles.twoColumnWrapper}>
        <div>
          <Text type="primary" bold fontSize="16px">{getZipcodeLabel(selectedAddress?.country)}</Text>
          {(selectedAddress?.zipcode) ? (
            <div {...getLongPressCopyProps(selectedAddress?.zipcode, '__zipcodeCopyTimer')}>
              <Text>{selectedAddress?.zipcode}</Text>
            </div>
          ) : (
            <div className={styles.missingValue}>MISSING</div>
          )}
          {/*<Text>{`${store.currentPractitioner?.city}, ${store.currentPractitioner?.state}`}</Text>*/}
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Country</Text>
          {selectedAddress?.country ? (
            <div {...getLongPressCopyProps(selectedAddress?.country, '__countryCopyTimer')}>
              <Text>{selectedAddress?.country}</Text>
            </div>
          ) : (
            <div className={styles.missingValue}>MISSING</div>
          )}
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Distance</Text>
          {/*<Text>{distance ? `${distance} miles away` : 'Calculating...'}</Text>*/}
          {lat == null || lon == null
            ? (<Text>Cannot Calculate: Missing Lat/Long.</Text>)
            : distance == null
              ? (<Text>Cannot calculate: did you allow app to use GPS?</Text>)
              : (<Text>{`${distance} miles away`}</Text>)}
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Lat & Long</Text>
          {(lat && lon) ? (
            <div
              onClick={() => {
                console.log('Clicked lat/lon. isLead:', isLead, 'is_visiting:', selectedAddress.is_visiting_address);
                if(isLead) {
                  console.log('Cannot edit - this is a lead');
                  return;
                }
                if(!canGeoValidate) {
                  return;
                }
                console.log('Triggering geo validation');
                handleGeoValidation();
              }}
              style={{
                cursor: (isLead || !canGeoValidate) ? 'default' : 'pointer',
                position: 'relative',
                display: 'inline-block',
                maxWidth: 'fit-content'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Text>{lat}</Text>
                  <Text>{lon}</Text>
                </div>
                {(!isLead && canGeoValidate) && (
                  <NotesIcon style={{
                    color: "#4F7D9C",
                    stroke: "#4F7D9C",
                    width: "16px",
                    height: "16px",
                    flexShrink: 0
                  }} />
                )}
              </div>
            </div>
          ) : (
            <div
              onClick={() => {
                if(isLead) return;
                if(!canGeoValidate) return;
                handleGeoValidation();
              }}
              className={isLead ? styles.missingValueLatLead : styles.missingValueLat}
            ><div>MISSING</div>
              {(!isLead && canGeoValidate) ? (<NotesIcon style={{ color: "white", stroke: "white" }} />) : null}
              </div>
          )}
          {/*<Text>{`${store.currentPractitioner?.latitude}, ${store.currentPractitioner?.longitude}`}</Text>*/}
        </div>
      </div>
      
      <div className={styles.streetViewSection}>
        <div 
          className={styles.streetViewBackground}
          style={{
            background: streetViewPhotos.length > 0 
              ? `url(${streetViewPhotos[0]})`
              : undefined,
            backgroundPosition: streetViewPhotos.length > 0 
              ? 'center 50%'
              : undefined
          }}
          onClick={streetViewPhotos.length > 0 ? () => setIsCarouselOpen(true) : undefined}
        >
          {streetViewPhotos.length === 0 && (
            <button 
              className={styles.streetViewButton}
              onClick={handleStreetViewClick}
              disabled={isLoadingStreetView || !selectedAddress?.latitude || !selectedAddress?.longitude}
            >
              {isLoadingStreetView ? 'LOADING...' : 'SHOW STREET VIEW'}
            </button>
          )}
        </div>
      </div>

      {isCarouselOpen && (
        <Modal
          zIndex={1000}
          onClose={() => setIsCarouselOpen(false)}
          classNames={{
            content: styles.carouselModalContent
          }}
          styles={{
            content: {
              backgroundColor: "transparent",
            },
            header: {
              display: "none",
              textAlign: "center",
            },
            body: {
              backgroundColor: "transparent",
              padding: "0",
              margin: "0",
              textAlign: "center",
              display: "flex",
              justifyContent: 'center'
            }
          }}
          opened={isCarouselOpen}>
          <div 
            className={styles.carouselContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setIsCarouselOpen(false)}
              style={{
                position: 'absolute',
                top: '0px',
                right: '0',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                zIndex: 1001,
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'white'
              }}
            >
              ×
            </button>
            <Carousel
              slideSize="100%"
              slideGap="md"
              withIndicators
              withControls
              emblaOptions={
                {
                  loop: true
                }
              }
              styles={{
                control: {
                  '&[data-inactive]': {
                    opacity: 0,
                    cursor: 'default',
                  },
                },
                indicator: {
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255)',
                  '&[data-active]': {
                    backgroundColor: 'white',
                  },
                },
              }}
            >
              {streetViewPhotos.map((photo, index) => (
                <Carousel.Slide key={index}>
                  <img 
                    src={photo}
                    alt={`Street view ${index + 1}`}
                    className={styles.carouselImage}
                  />
                </Carousel.Slide>
              ))}
            </Carousel>
          </div>
        </Modal>
      )}
      <div className={styles.sectionWrapper}>
        <p className={styles.headerText}>ACCOUNT DETAILS</p>
      </div>

      <div className={styles.singleColumnWrapper}>
        <div>
          <Text type="primary" bold fontSize="16px">Company Name</Text>
          {/*<Text>{store.currentPractitioner?.email}</Text>*/}
          {store.currentPractitioner?.companyname ? (
            <div {...getLongPressCopyProps(store.currentPractitioner?.companyname, '__companyNameCopyTimer')}>
              <Text>{store.currentPractitioner?.companyname}</Text>
            </div>
          ) : (
            <div className={styles.missingValue}>Missing</div>
          )}
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Email</Text>
          {/*<Text>{store.currentPractitioner?.email}</Text>*/}
          {store.currentPractitioner?.email ? (
            <span style={{ cursor: 'pointer' }} onClick={() => setActionSheetType('email')}>
              <Text>{store.currentPractitioner?.email}</Text>
            </span>
          ) : (
            <div className={styles.missingValue}>Missing</div>
          )}
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Account Owner</Text>
          {store.currentPractitioner?.fmc ? (
            <div {...getLongPressCopyProps(store.currentPractitioner?.fmc, '__accountOwnerCopyTimer')}>
              <Text>{store.currentPractitioner?.fmc}</Text>
            </div>
          ) : (
            <div className={styles.missingValue}>Missing</div>
          )}
        </div>

        <div>
          <Text type="primary" bold fontSize="16px">Ready to Purchase</Text>
          <Text>{store.currentPractitioner?.ready_to_purchase ? "Yes" : "No"}</Text>
        </div>
        {/* <div>
          <Text type="primary" bold fontSize="16px">Stage</Text>
          {store.currentPractitioner?.stage ? (
            <Text>{store.currentPractitioner?.stage}</Text>
          ) : (
            <div className={styles.missingValue}>Missing</div>
          )}
        </div> */}
        <div>
          <Text type="primary" bold fontSize="16px">Status</Text>
          {store.currentPractitioner?.status ? (
            ['LEAD-INCOMPLETE', 'LEAD-READY2SUBMIT', 'LEAD-AWAITING-NETSUITE'].includes(store.currentPractitioner.status) ? (
              <div {...getLongPressCopyProps(store.currentPractitioner.status, '__statusCopyTimer')}>
                <Text>{store.currentPractitioner.status}</Text>
              </div>
            ) : (
              store.currentPractitioner?.stage ? (
                <div {...getLongPressCopyProps(`${store.currentPractitioner.stage}-${store.currentPractitioner.status}`, '__statusCopyTimer')}>
                  <Text>{`${store.currentPractitioner.stage}-${store.currentPractitioner.status}`}</Text>
                </div>
              ) : (
                <div className={styles.missingValue}>Missing</div>
              )
            )
          ) : (
            <div className={styles.missingValue}>Missing</div>
          )}
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Specialty</Text>
          {store.currentPractitioner?.specialty ? (
            <div {...getLongPressCopyProps(store.currentPractitioner?.specialty, '__specialtyCopyTimer')}>
              <Text>{store.currentPractitioner?.specialty}</Text>
            </div>
          ) : (
            <div className={styles.missingValue}>Missing</div>
          )}
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Practice Type</Text>
          {store.currentPractitioner?.practice_type ? (
            <div {...getLongPressCopyProps(store.currentPractitioner.practice_type, '__practiceTypeCopyTimer')}>
              <Text>{store.currentPractitioner.practice_type}</Text>
            </div>
          ) : (
            <div className={styles.missingValue}>Missing</div>
          )}
        </div>
        <div>
          <Text type="primary" bold fontSize="16px">Website</Text>
          {store.currentPractitioner?.website ? (
            <span style={{ cursor: 'pointer' }} onClick={() => setActionSheetType('website')}>
              <Text>{store.currentPractitioner?.website}</Text>
            </span>
          ) : (
            <div className={styles.missingValue}>Missing</div>
          )}
        </div>
      </div>
      <div className={styles.sectionWrapper}>
        <p className={styles.headerText}>Data Source</p>
      </div>
      <div className={styles.twoColumnWrapper}>
        <div>
          <Text type="primary" bold fontSize="16px">Data Source</Text>
          {/*<Text>{getDataSourceLabel(store.currentPractitioner?.data_source)}</Text>*/}
          <Text>
            {(
              store.currentPractitioner?.status === "LEAD-INCOMPLETE" ||
              store.currentPractitioner?.status === "LEAD-READY2SUBMIT"
            )
              ? "Lead created with Sales Assistant"
              : getDataSourceLabel(store.currentPractitioner?.data_source)
            }
          </Text>
        </div>
        {getChannelLabel(store.currentPractitioner?.name_dfh_channel) && (
          <div>
            <Text type="primary" bold fontSize="16px"> </Text>
            <Text>{getChannelLabel(store.currentPractitioner?.name_dfh_channel)}</Text>
          </div>
        )}
        <div>
          <Text type="primary" bold fontSize="16px">NETSUITE ID</Text>
          {(store?.currentPractitioner?.netsuite_id) ? (
            <div {...getLongPressCopyProps(store?.currentPractitioner?.netsuite_id, '__netsuiteCopyTimer')}>
              <Text>{store?.currentPractitioner?.netsuite_id}</Text>
            </div>
          ) : (
            <div className={styles.missingValue}>MISSING</div>
          )}
        </div>
      </div>

      <CopyActionSheet
        isOpen={actionSheetType !== null}
        type={actionSheetType || 'email'}
        value={
          actionSheetType === 'email' ? (store.currentPractitioner?.email || '') :
          actionSheetType === 'website' ? (store.currentPractitioner?.website || '') :
          (phoneNumber || '')
        }
        onClose={() => setActionSheetType(null)}
        onCopy={() => {
          const valueToCopy =
            actionSheetType === 'email' ? store.currentPractitioner?.email :
            actionSheetType === 'website' ? store.currentPractitioner?.website :
            phoneNumber;
          if (valueToCopy) {
            copyToClipboard(valueToCopy);
            setShowCopyToast(true);
          }
        }}
        onAction={() => {
          if (actionSheetType === 'email') {
            handleContact('email');
          } else if (actionSheetType === 'website') {
            const website = store.currentPractitioner?.website;
            const url = website?.startsWith('http://') || website?.startsWith('https://')
              ? website
              : `https://${website}`;
            window.open(url, '_blank');
          } else {
            handleContact('phone', phoneNumber || undefined);
          }
        }}
      />

      <Toastr
        message="Copied!"
        isVisible={showCopyToast}
        onClose={() => setShowCopyToast(false)}
        backgroundColor="white"
        closeTime={1500}
      />
    </div>
  )
})

export default PractitionerDetails;